import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Shield, ArrowLeft, Download, Clock, User, Settings, UserX, UserCheck, Key, Trash2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const ACTION_CONFIG = {
  user_suspended: { label: "User Suspended", icon: UserX, color: "text-red-400" },
  user_unsuspended: { label: "User Unsuspended", icon: UserCheck, color: "text-green-400" },
  password_reset: { label: "Password Reset", icon: Key, color: "text-yellow-400" },
  user_deleted: { label: "User Deleted", icon: Trash2, color: "text-red-400" },
  tool_transferred: { label: "Tool Transferred", icon: ArrowRightLeft, color: "text-blue-400" },
  tool_status_changed: { label: "Tool Status Changed", icon: Settings, color: "text-primary" },
  tool_deleted: { label: "Tool Deleted", icon: Trash2, color: "text-red-400" },
  settings_updated: { label: "Settings Updated", icon: Settings, color: "text-primary" }
};

export default function AdminLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("");

  useEffect(() => {
    fetchLogs();
  }, [actionFilter]);

  const fetchLogs = async () => {
    try {
      const params = new URLSearchParams();
      if (actionFilter && actionFilter !== "all") params.append("action", actionFilter);
      params.append("limit", "200");

      const response = await fetch(`${API}/admin/logs?${params}`, { headers: getAuthHeaders() });

      if (!response.ok) {
        if (response.status === 403) {
          toast.error("Admin access required");
          navigate("/dashboard");
          return;
        }
        throw new Error("Failed to fetch logs");
      }

      const data = await response.json();
      setLogs(data.logs);
      setTotal(data.total);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await fetch(`${API}/admin/export/activity`, { headers: getAuthHeaders() });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `admin_activity_log_${new Date().toISOString().split("T")[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Activity log exported!");
      }
    } catch (error) {
      toast.error("Export failed");
    }
  };

  const formatTimestamp = (ts) => {
    const date = new Date(ts);
    return date.toLocaleString();
  };

  const getActionDetails = (log) => {
    if (log.old_status && log.new_status) {
      return `${log.old_status} → ${log.new_status}`;
    }
    if (log.from_user_id && log.to_user_id) {
      return `From ${log.from_user_id.slice(-8)} to ${log.to_user_id.slice(-8)}`;
    }
    if (log.changes) {
      // Handle changes as array, string, or object
      if (Array.isArray(log.changes)) {
        return log.changes.join(", ");
      } else if (typeof log.changes === 'string') {
        return log.changes;
      } else if (typeof log.changes === 'object') {
        return JSON.stringify(log.changes);
      }
    }
    if (log.target_email) {
      return log.target_email;
    }
    return "";
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-2" />Admin</Button>
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-primary" />
              <span className="font-heading text-lg font-bold uppercase">Activity Logs</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} className="border-secondary" data-testid="export-logs-btn">
            <Download className="w-4 h-4 mr-2" />Export CSV
          </Button>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-5xl mx-auto">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-full sm:w-[220px] bg-card" data-testid="action-filter">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="user_suspended">User Suspended</SelectItem>
              <SelectItem value="user_unsuspended">User Unsuspended</SelectItem>
              <SelectItem value="password_reset">Password Reset</SelectItem>
              <SelectItem value="user_deleted">User Deleted</SelectItem>
              <SelectItem value="tool_transferred">Tool Transferred</SelectItem>
              <SelectItem value="tool_status_changed">Tool Status Changed</SelectItem>
              <SelectItem value="tool_deleted">Tool Deleted</SelectItem>
              <SelectItem value="settings_updated">Settings Updated</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-muted-foreground text-sm self-center">Showing {logs.length} of {total} entries</p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading logs...</p>
          </div>
        ) : logs.length === 0 ? (
          <Card className="border-border bg-card">
            <CardContent className="py-12 text-center">
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <p className="text-muted-foreground">No activity logs yet</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {logs.map((log, index) => {
              const config = ACTION_CONFIG[log.action] || { label: log.action, icon: Clock, color: "text-muted-foreground" };
              const Icon = config.icon;
              return (
                <Card key={index} className="border-border bg-card">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded bg-secondary/30 ${config.color}`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant="outline" className={config.color}>{config.label}</Badge>
                          <span className="text-xs text-muted-foreground">{formatTimestamp(log.timestamp)}</span>
                        </div>
                        <div className="mt-1 text-sm space-y-1">
                          {log.target_user_id && (
                            <p className="text-muted-foreground">
                              <span className="text-foreground/70">Target User:</span>{" "}
                              <span className="font-mono text-xs">{log.target_user_id}</span>
                            </p>
                          )}
                          {log.tool_id && (
                            <p className="text-muted-foreground">
                              <span className="text-foreground/70">Tool:</span>{" "}
                              <span className="font-mono text-xs">{log.tool_id}</span>
                            </p>
                          )}
                          {getActionDetails(log) && (
                            <p className="text-muted-foreground">
                              <span className="text-foreground/70">Details:</span> {getActionDetails(log)}
                            </p>
                          )}
                          <p className="text-muted-foreground">
                            <span className="text-foreground/70">By Admin:</span>{" "}
                            <span className="font-mono text-xs">{log.admin_id}</span>
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
